import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import {
  AssetDefaultOrSelectedTypeMap,
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../store/product/types';

import { PublicOfferFormDataT4, getValidationCreateSchemaT4 } from './constants';

export const usePublicOfferAssetFixedController = (
  offer: AssetDefaultOrSelectedTypeMap['publicOffers'],
  _: PublicOfferAssetToInvestNormalized,
  modality: PublicOfferDetailsModalityWithTimelineNormalized
) => {
  const minimumValue = modality.minimumApplication;
  const maximumValue = modality.maximumApplication;

  const remuneration = offer.maximumRemuneration;

  const [isValueFocused, setIsValueFocused] = useState(false);
  const [valueApplied, setValueApplied] = useState(minimumValue);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(minimumValue);
  const remunerationFormatted = Utils.Mask.Percentage(remuneration);

  const tableForm = useForm<PublicOfferFormDataT4>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      priceItem: minValueFormatted,
    },
    resolver: zodResolver(getValidationCreateSchemaT4(minimumValue, maximumValue)),
  });

  const handleInputValueFocus = () => {
    setIsValueFocused(true);
    if (tableForm.formState.errors?.priceItem) {
      tableForm.resetField('priceItem');
    }
  };

  const handleInputValueClear = () => {
    setIsValueFocused(false);
    tableForm.resetField('priceItem');
    tableForm.clearErrors('priceItem');
  };

  const handleInputValueBlur = async () => {
    const currentValue = tableForm.getValues('priceItem');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    if (currentValueUnformatted >= minimumValue && currentValueUnformatted <= maximumValue) {
      tableForm.setValue('priceItem', currentValue);
      setValueApplied(currentValueUnformatted);
    } else {
      tableForm.clearErrors('priceItem');
      tableForm.setValue('priceItem', Utils.Mask.Money(minimumValue));
      setValueApplied(minimumValue);
    }

    if (isValueFocused) {
      setIsValueFocused(false);
    }
  };

  const handleSubmit = async ({ priceItem }: PublicOfferFormDataT4) => {
    const priceItemUnformatted = Utils.Unmask.Money(priceItem);
    tableForm.setValue('priceItem', priceItem);
    setValueApplied(priceItemUnformatted);

    setIsValueFocused(false);
  };

  return {
    minimumValue,
    handleInputValueFocus,
    handleInputValueClear,
    isValueFocused,
    handleInputValueBlur,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    hasErrorValue: tableForm.formState.errors?.priceItem,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    currentRemuneration: remuneration,
    currentRemunerationFormatted: remunerationFormatted,
  };
};

export type PublicOfferFixedController = ReturnType<typeof usePublicOfferAssetFixedController>;
